import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Upload, X } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import useCategoryStore from '../../store/categoryStore';
import useExpenseStore from '../../store/expenseStore';
import useInitializeFamily from '../../hooks/useInitializeFamily';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import TagSelector from '../../components/shared/TagSelector';
import { PRIORITY_TYPES } from '../../utils/constants';
import { resetCategoriesToLatest } from '../../utils/resetCategories';

const expenseSchema = z.object({
  amount: z.string().min(1, 'Amount is required').refine((val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0, {
    message: 'Amount must be a positive number',
  }),
  description: z.string().min(1, 'Description is required'),
  category: z.string().min(1, 'Category is required'),
  subcategory: z.string().optional(),
  priority: z.enum([PRIORITY_TYPES.NEED, PRIORITY_TYPES.WANT, PRIORITY_TYPES.SAVINGS]),
  date: z.string().min(1, 'Date is required'),
  notes: z.string().optional(),
});

const AddExpense = () => {
  // Initialize family data (categories, etc.)
  useInitializeFamily();
  
  const navigate = useNavigate();
  const { userProfile } = useAuthStore();
  const { categories } = useCategoryStore();
  const { createExpense, loading } = useExpenseStore();
  const [receiptFile, setReceiptFile] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      priority: PRIORITY_TYPES.NEED,
    },
  });

  const selectedCategory = watch('category');
  const selectedSubcategory = watch('subcategory');
  
  // Deduplicate categories by name to avoid showing duplicates
  const uniqueCategories = categories.filter((category, index, self) => 
    index === self.findIndex(c => c.name === category.name)
  );
  
  const selectedCategoryData = uniqueCategories.find(cat => cat.id === selectedCategory);
  
  console.log('Categories in AddExpense:', categories.length, 'Unique:', uniqueCategories.length);
  console.log('Selected category data:', selectedCategoryData);

  const onSubmit = async (data) => {
    if (!userProfile?.familyId) {
      setError('No family found. Please try again.');
      return;
    }

    setError('');

    const expenseData = {
      amount: parseFloat(data.amount),
      description: data.description,
      category: data.category,
      subcategory: data.subcategory || null,
      priority: data.priority,
      date: new Date(data.date),
      notes: data.notes || '',
      tags: selectedTags,
      paidBy: userProfile.uid,
      isRecurring: false,
      isSplit: false,
    };

    try {
      const result = await createExpense(
        userProfile.familyId,
        userProfile.uid,
        expenseData,
        receiptFile
      );

      if (result.success) {
        navigate('/expenses');
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to create expense. Please try again.');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      setReceiptFile(file);
      setError('');
    }
  };

  const removeReceipt = () => {
    setReceiptFile(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Link to="/expenses">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Add Expense</h1>
          <p className="text-gray-600">Record a new expense for your family</p>
          {categories.length > 10 && (
            <p className="text-sm text-orange-600 mt-1">
              ⚠️ {categories.length} categories loaded (should be 10) - duplicates detected
            </p>
          )}
        </div>
        {categories.length > 10 && (
          <Button 
            onClick={async () => {
              if (userProfile?.familyId) {
                console.log('🔄 Resetting categories from AddExpense page...');
                const result = await resetCategoriesToLatest(userProfile.familyId, userProfile.uid);
                if (result.success) {
                  window.location.reload();
                }
              }
            }}
            variant="outline"
            size="sm"
          >
            Fix Categories
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New Expense</CardTitle>
          <CardDescription>
            Fill in the details for your expense
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Debug info for categories */}
          {categories.length !== 10 && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md text-sm mb-4">
              <strong>Debug Info:</strong> {categories.length} categories loaded (expected: 10)
              <br />
              <strong>Unique categories:</strong> {uniqueCategories.length}
              <br />
              <strong>Categories:</strong> {uniqueCategories.map(cat => cat.name).join(', ')}
            </div>
          )}
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Amount */}
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                  Amount *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">৳</span>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className={`pl-8 ${errors.amount ? 'border-red-500' : ''}`}
                    {...register('amount')}
                  />
                </div>
                {errors.amount && (
                  <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
                )}
              </div>

              {/* Date */}
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <Input
                  id="date"
                  type="date"
                  className={errors.date ? 'border-red-500' : ''}
                  {...register('date')}
                />
                {errors.date && (
                  <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <Input
                id="description"
                type="text"
                placeholder="What did you spend money on?"
                className={errors.description ? 'border-red-500' : ''}
                {...register('description')}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            {/* Category Selection */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                id="category"
                className={`w-full h-10 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.category ? 'border-red-500' : 'border-gray-300'
                }`}
                {...register('category')}
              >
                <option value="">Select a category</option>
                {uniqueCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
              )}
              {selectedCategoryData && (
                <div className="mt-2 flex items-center space-x-2">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: selectedCategoryData.color }}
                  />
                  <span className="text-sm text-gray-600">
                    {selectedCategoryData.icon} {selectedCategoryData.name}
                    {watch('subcategory') && ` • ${watch('subcategory')}`}
                  </span>
                </div>
              )}
            </div>

            {/* Subcategory Selection - Button Group */}
            {selectedCategoryData && selectedCategoryData.subcategories && selectedCategoryData.subcategories.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subcategory (Optional)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {selectedCategoryData.subcategories.map((subcategory, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        const currentValue = watch('subcategory');
                        const newValue = currentValue === subcategory.name ? '' : subcategory.name;
                        setValue('subcategory', newValue);
                      }}
                      className={`p-3 text-sm border rounded-lg transition-all duration-200 flex flex-col items-center space-y-1 hover:scale-105 ${
                        watch('subcategory') === subcategory.name
                          ? 'border-primary bg-primary text-white shadow-md'
                          : 'border-gray-300 hover:border-primary hover:bg-primary/5'
                      }`}
                    >
                      <span className="text-lg">{subcategory.icon}</span>
                      <span className="text-xs text-center leading-tight">{subcategory.name}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-500">
                    Click to select/deselect a subcategory
                  </p>
                  {watch('subcategory') && (
                    <button
                      type="button"
                      onClick={() => setValue('subcategory', '')}
                      className="text-xs text-primary hover:text-primary/80"
                    >
                      Clear selection
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Priority Selection - Button Group */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority *
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setValue('priority', PRIORITY_TYPES.NEED)}
                  className={`p-4 text-sm border rounded-lg transition-colors flex flex-col items-center space-y-1 ${
                    watch('priority') === PRIORITY_TYPES.NEED
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-lg">🔴</span>
                  <span className="font-medium">Need</span>
                  <span className="text-xs text-center">Essential expense</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => setValue('priority', PRIORITY_TYPES.WANT)}
                  className={`p-4 text-sm border rounded-lg transition-colors flex flex-col items-center space-y-1 ${
                    watch('priority') === PRIORITY_TYPES.WANT
                      ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-lg">🟡</span>
                  <span className="font-medium">Want</span>
                  <span className="text-xs text-center">Nice to have</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => setValue('priority', PRIORITY_TYPES.SAVINGS)}
                  className={`p-4 text-sm border rounded-lg transition-colors flex flex-col items-center space-y-1 ${
                    watch('priority') === PRIORITY_TYPES.SAVINGS
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-lg">🟢</span>
                  <span className="font-medium">Savings</span>
                  <span className="text-xs text-center">Investment/Emergency</span>
                </button>
              </div>
              {errors.priority && (
                <p className="mt-1 text-sm text-red-600">{errors.priority.message}</p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                id="notes"
                rows={3}
                placeholder="Any additional details about this expense..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                {...register('notes')}
              />
            </div>

            {/* Tags */}
            <TagSelector
              selectedTags={selectedTags}
              onTagsChange={setSelectedTags}
            />

            {/* Receipt Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Receipt (Optional)
              </label>
              
              {!receiptFile ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    Upload a receipt image
                  </p>
                  <p className="text-xs text-gray-500 mb-4">
                    PNG, JPG up to 5MB
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="receipt-upload"
                  />
                  <label htmlFor="receipt-upload">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span>Choose File</span>
                    </Button>
                  </label>
                </div>
              ) : (
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          📄
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {receiptFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(receiptFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeReceipt}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex space-x-4 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Adding Expense...' : 'Add Expense'}
              </Button>
              <Link to="/expenses" className="flex-1">
                <Button type="button" variant="outline" className="w-full">
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddExpense;
